import React, {useEffect, useState} from "react";
import {Form} from "react-bootstrap";
import {ErrorToast, SuccessToast} from "../../shared/toasters/toasters";
import {AdminBrandService} from "./service";
import {useTranslation} from "react-i18next";
import {CreateForm} from "./components/CreateForm";
import {List} from "./components/List";
import DraggableBootstrapTable from "../../shared/components/draggableList/Draggable";
import {Pagination} from "../../shared/paginations/Paginations";
import Select, {SingleValue} from "react-select";
import {handleBrandIsActive} from "../../shared/functions/handleBrandIsActive";
import {IBrand} from "./interface";

export const AdminBrand = () => {
    const {t} = useTranslation();
    const [isMenuOpened, setIsMenuOpened] = useState(false);
    const [image, setImage] = useState<File | null>(null);
    const [currentBrand, setCurrentBrand] = useState<IBrand | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 25,
        term: ""
    });
    const perPageOptions = [
        {
            label: 10,
            value: 10,
        },
        {
            label: 25,
            value: 25,
        },
        {
            label: 50,
            value: 50,
        },
    ];
    const [show, setShow] = useState(false);
    const handleShow = () => setShow(true);
    const [selectedPrompt, setSelectedPrompt] = useState<IBrand | null>(null);
    const [brandsList, setBrandsList] = useState<IBrand[]>([]);
    const handlePages = (updatePage: number) => {
        setPagination({...pagination, page: updatePage});
        setPage(updatePage);
    };
    const handlePerPage = (e: SingleValue<{ label: number; value: number; }>) => {
        setPagination((prev) => ({...prev, page: 1, perPage: Number(e?.value)}));
    };
    const updatePosition = (newList: IBrand[]) => {
        const newListUpdated = newList?.map((item) => item.id) as number[];
        AdminBrandService.updatePosition(newListUpdated);
    };
    const editHandler = (brandId: number) => {
        setIsMenuOpened(true);
        AdminBrandService.getSingle(brandId)
            .then((response) => {
                const {data} = response;
                setCurrentBrand(data);
                window.scrollTo(0, 0);
            })
            .catch(ErrorToast);
    };
    const submitHandler = () => {
        const formData = new FormData();
        formData.append("body", JSON.stringify({
            ...currentBrand,
            category: currentBrand?.category?.id ? currentBrand?.category?.id : currentBrand?.category
        }));
        image && formData.append("image", image);
        if (currentBrand?.id) {
            AdminBrandService.update(formData, currentBrand.id)
                .then(() => {
                    SuccessToast(t(`brand.successfully_updated`));
                    setIsMenuOpened(false);
                    setPagination(prev => ({...prev, updatedAt: new Date()?.getTime()}))
                    setCurrentBrand(null);
                })
                .catch(ErrorToast);
        } else {
            AdminBrandService.create(formData)
                .then(() => {
                    SuccessToast(t(`brand.successfully_created`));
                    setIsMenuOpened(false);
                    setPagination(prev => ({...prev, updatedAt: new Date()?.getTime()}))
                    setCurrentBrand(null);
                })
                .catch(ErrorToast);
        }
    };
    const deleteHandler = () => {
        selectedPrompt?.id &&
        AdminBrandService.delete(selectedPrompt?.id)
            .then((response) => {
                if (response?.status === "success") {
                    SuccessToast(t(`brand.successfully_deleted`));
                    setPagination((prev) => ({...prev, updatedAt: new Date().getTime()}))
                }
            })
            .catch(ErrorToast);
    };
    useEffect(() => {
        AdminBrandService.getAll(pagination)
            .then((response) => {
                const {data} = response;
                setBrandsList(data?.items);
                setTotalPages(Math.ceil(data?.totalItems / data?.perPage));
            })
            .catch(ErrorToast);
    }, [pagination]);
    return (
        <>
            {!isMenuOpened && (
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="row g-2">
                                <div className="d-flex justify-content-between">
                                    <div className="d-flex gap-3">
                                        <div className="search-box">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="searchMemberList"
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    setPagination((prev) => ({
                                                        ...prev,
                                                        term: e.target.value,
                                                    }))
                                                }
                                                placeholder={t("global.search")}
                                            />
                                            <i className="ri-search-line search-icon"></i>
                                        </div>
                                        <div className="d-flex">
                                            <h6 className="my-auto me-2">{t('table_headers.per_page')}:</h6>
                                            <Select
                                                className="react-select-field"
                                                onChange={handlePerPage}
                                                placeholder="Per page"
                                                options={perPageOptions}
                                                defaultValue={perPageOptions[1]}
                                            />
                                        </div>
                                    </div>
                                    <div className="list-grid-nav">
                                        <button
                                            className="btn btn-info"
                                            onClick={() => setIsMenuOpened(true)}
                                        >
                                            {t("global.add")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isMenuOpened && (
                <div className="col-12">
                    <Form
                        onSubmit={(e) => {
                            e.preventDefault();
                            submitHandler();
                        }}
                    >
                        <CreateForm
                            setIsMenuOpened={setIsMenuOpened}
                            state={currentBrand}
                            setState={setCurrentBrand}
                            setImage={setImage}
                        />
                    </Form>
                </div>
            )}
            {page > 1 || pagination?.term ? (
                <>
                    <div className="col-12">
                        <List
                            brandsList={brandsList}
                            editHandler={editHandler}
                            deleteHandler={deleteHandler}
                            selectedPrompt={selectedPrompt}
                            setSelectedPrompt={setSelectedPrompt}
                            show={show}
                            setShow={setShow}
                        />
                    </div>
                    {" "}
                </>
            ) : (
                <div className="col-12">
                    <DraggableBootstrapTable
                        preview={true}
                        list={brandsList!}
                        setList={setBrandsList}
                        headers={["id", "name", "category", "active"]}
                        title="brand"
                        onEdit={editHandler}
                        selectedPrompt={selectedPrompt}
                        setSelectedPrompt={setSelectedPrompt}
                        handleShow={handleShow}
                        show={show}
                        setShow={setShow}
                        deleteHandler={deleteHandler}
                        updatePosition={updatePosition}
                        toggleIsActive={(id) => handleBrandIsActive(id, setBrandsList, t)}
                    />
                </div>
            )}
            {page && (
                <div className="d-flex justify-content-end">
                    {totalPages <= 1 ? (
                        ""
                    ) : (
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            handlePagination={handlePages}
                        />
                    )}
                </div>
            )}
        </>
    );
};
